// dev-portal/src/pages/marketplace/MarketplaceCategoryView.jsx

import React, { useState, useEffect } from 'react';
import { api } from '../../api/client';
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom';
import {
    Package, Star, Download, ChevronRight, ArrowLeft,
    Filter, Search
} from 'lucide-react';
import MarketplaceLayout from '../../layouts/MarketplaceLayout';

export default function MarketplaceCategoryView() {
    const { slug } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const subcategoryParam = searchParams.get('subcategory');
    const navigate = useNavigate();
    const [category, setCategory] = useState(null);
    const [apps, setApps] = useState([]);
    const [filteredApps, setFilteredApps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('name');
    const [selectedSubcategory, setSelectedSubcategory] = useState(subcategoryParam || 'all');

    useEffect(() => {
        fetchCategoryApps();
    }, [slug, subcategoryParam]);

    useEffect(() => {
        filterAndSortApps();
    }, [apps, searchTerm, sortBy]);

    const fetchCategoryApps = async () => {
        try {
            const params = subcategoryParam ? { subcategory: subcategoryParam } : {};
            const response = await api.get(`/api/marketplace/apps/category/${slug}`, { params });
            setCategory(response.data.category);
            setApps(response.data.apps);
        } catch (error) {
            console.error('Failed to fetch category apps:', error);
            if (error.response?.status === 404) {
                navigate('/marketplace/categories');
            }
        } finally {
            setLoading(false);
        }
    };

    const filterAndSortApps = () => {
        let filtered = [...apps];

        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(app =>
                app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                app.description?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Sort
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'name':
                    return a.name.localeCompare(b.name);
                case 'price_low':
                    return (a.price || 0) - (b.price || 0);
                case 'price_high':
                    return (b.price || 0) - (a.price || 0);
                case 'rating':
                    return (b.average_rating || 0) - (a.average_rating || 0);
                default:
                    return 0;
            }
        });

        setFilteredApps(filtered);
    };

    const handleAppClick = (app) => {
        const identifier = app.slug || app.id;
        navigate(`/marketplace/app/${identifier}`);
    };

    const handleSubcategoryChange = (subcategorySlug) => {
        setSelectedSubcategory(subcategorySlug);
        if (subcategorySlug === 'all') {
            navigate(`/marketplace/category/${slug}`);
        } else {
            navigate(`/marketplace/category/${slug}?subcategory=${subcategorySlug}`);
        }
    };

    if (loading) {
        return (
            <MarketplaceLayout>
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </MarketplaceLayout>
        );
    }

    if (!category) {
        return (
            <MarketplaceLayout>
                <div className="text-center py-12">
                    <p className="text-gray-600">Category not found</p>
                </div>
            </MarketplaceLayout>
        );
    }

    const currentSubcategory = category.subcategories?.find(sc => sc.slug === subcategoryParam);

    return (
        <MarketplaceLayout>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Breadcrumb */}
                <nav className="mb-6">
                    <ol className="flex items-center space-x-2 text-sm">
                        <li>
                            <Link to="/marketplace" className="text-gray-500 hover:text-gray-700">
                                Marketplace
                            </Link>
                        </li>
                        <li>
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                        </li>
                        <li>
                            <Link to="/marketplace/categories" className="text-gray-500 hover:text-gray-700">
                                Categories
                            </Link>
                        </li>
                        <li>
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                        </li>
                        <li>
                            <Link
                                to={`/marketplace/category/${slug}`}
                                className={currentSubcategory ? "text-gray-500 hover:text-gray-700" : "text-gray-900 font-medium"}
                            >
                                {category.name}
                            </Link>
                        </li>
                        {currentSubcategory && (
                            <>
                                <li>
                                    <ChevronRight className="w-4 h-4 text-gray-400" />
                                </li>
                                <li className="text-gray-900 font-medium">{currentSubcategory.name}</li>
                            </>
                        )}
                    </ol>
                </nav>

                {/* Category Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-4 mb-4">
                        <div
                            className="w-16 h-16 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: category.color + '20' }}
                        >
                            <Package
                                className="w-10 h-10"
                                style={{ color: category.color }}
                            />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">
                                {currentSubcategory ? currentSubcategory.name : category.name}
                            </h1>
                            <p className="text-gray-600">{category.description}</p>
                        </div>
                    </div>
                </div>

                <div className="flex gap-6">
                    {/* Left Sidebar - Subcategory Filter */}
                    {category.subcategories && category.subcategories.length > 0 && (
                        <div className="w-64 flex-shrink-0">
                            <div className="bg-white rounded-lg shadow p-4 sticky top-4">
                                <h2 className="text-sm font-semibold text-gray-900 mb-3">Filter by subcategory</h2>
                                <nav className="space-y-1">
                                    <button
                                        onClick={() => handleSubcategoryChange('all')}
                                        className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${selectedSubcategory === 'all'
                                                ? 'bg-blue-50 text-blue-700 font-medium'
                                                : 'text-gray-700 hover:bg-gray-50'
                                            }`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span>All</span>
                                        </div>
                                    </button>
                                    {category.subcategories.map((subcat) => (
                                        <button
                                            key={subcat.id}
                                            onClick={() => handleSubcategoryChange(subcat.slug)}
                                            className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${selectedSubcategory === subcat.slug
                                                    ? 'bg-blue-50 text-blue-700 font-medium'
                                                    : 'text-gray-700 hover:bg-gray-50'
                                                }`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <span>{subcat.name}</span>
                                                <span className="text-xs text-gray-500">{subcat.app_count}</span>
                                            </div>
                                        </button>
                                    ))}
                                </nav>
                            </div>
                        </div>
                    )}

                    {/* Main Content */}
                    <div className="flex-1">
                        {/* Search and Filter Bar */}
                        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
                            <div className="flex flex-col md:flex-row gap-4">
                                <div className="flex-1 relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                    <input
                                        type="text"
                                        placeholder={`Search ${currentSubcategory ? currentSubcategory.name : category.name} apps...`}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div className="flex items-center gap-2">
                                    <Filter className="text-gray-400 w-5 h-5" />
                                    <select
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value)}
                                        className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="name">Sort by Name</option>
                                        <option value="price_low">Price: Low to High</option>
                                        <option value="price_high">Price: High to Low</option>
                                        <option value="rating">Highest Rated</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Results Count */}
                        <div className="mb-4">
                            <p className="text-gray-600">
                                Showing {filteredApps.length} {filteredApps.length === 1 ? 'app' : 'apps'}
                                {currentSubcategory && ` in ${currentSubcategory.name}`}
                            </p>
                        </div>

                        {/* Apps Grid */}
                        {filteredApps.length === 0 ? (
                            <div className="text-center py-12 bg-white rounded-lg">
                                <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-600">No apps found matching your criteria</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {filteredApps.map(app => (
                                    <AppCard key={app.id} app={app} onClick={() => handleAppClick(app)} />
                                ))}
                            </div>
                        )}

                        {/* Back to Categories */}
                        <div className="mt-8 text-center">
                            <Link
                                to="/marketplace/categories"
                                className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                Back to all categories
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </MarketplaceLayout>
    );
}

// App Card Component
const AppCard = ({ app, onClick }) => {
    return (
        <div
            onClick={onClick}
            className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-all cursor-pointer group border border-gray-200 hover:border-blue-300"
        >
            <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-3">
                    {app.icon_url ? (
                        <img src={app.icon_url} alt={app.name} className="w-14 h-14 rounded-lg" />
                    ) : (
                        <div className="w-14 h-14 bg-gray-200 rounded-lg flex items-center justify-center">
                            <Package className="w-7 h-7 text-gray-500" />
                        </div>
                    )}

                    {/* App Type Badge */}
                    {app.type && (
                        <span className={`px-2 py-1 rounded text-xs font-medium ${app.type === 'internal'
                                ? 'bg-gray-100 text-gray-700'
                                : app.type === 'partner'
                                    ? 'bg-purple-100 text-purple-700'
                                    : 'bg-blue-100 text-blue-700'
                            }`}>
                            {app.type.charAt(0).toUpperCase() + app.type.slice(1)}
                        </span>
                    )}
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">{app.name}</h3>

            <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                {app.short_description || app.description}
            </p>

            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 text-sm text-gray-500">
                    {app.average_rating > 0 && (
                        <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 text-yellow-500 fill-current" />
                            <span>{app.average_rating.toFixed(1)}</span>
                        </div>
                    )}
                    {app.installations > 0 && (
                        <div className="flex items-center gap-1">
                            <Download className="w-4 h-4" />
                            <span>{app.installations}</span>
                        </div>
                    )}
                </div>

                <div className="font-semibold text-gray-900">
                    {app.pricing_model === 'free' ? (
                        <span className="text-green-600">Free</span>
                    ) : (
                        <span>${app.price}</span>
                    )}
                </div>
            </div>
        </div>
    );
};